import type { ReactNode } from 'react'

import { type SizingProps } from '@material-ui/system'

import { useIsPdfViewerIframeEmbedEnabled } from './PdfViewerIframeEmbedContext'
import { PdfViewerStyled } from './styles/PdfViewerStyled'
import { isWebKitBrowser } from './utils/isWebKitBrowser'

export interface IPdfViewerProps extends SizingProps {
  /**
   * URL of the PDF to embed.
   */
  data: string
  /**
   * Rendered when the PDF fails to load, using the `<object>` element's
   * fallback content. It does not render on WebKit browsers, which get an
   * `<iframe>` instead.
   */
  children?: ReactNode
}

export const PdfViewer = ({
  data,
  children,
  ...otherProps
}: IPdfViewerProps) => {
  const isIframeEmbedEnabled = useIsPdfViewerIframeEmbedEnabled()
  const shouldRenderIframe =
    isIframeEmbedEnabled && isWebKitBrowser(navigator.userAgent)

  if (shouldRenderIframe) {
    return (
      <PdfViewerStyled {...otherProps}>
        <iframe width='100%' height='100%' title='PDF' src={data} />
      </PdfViewerStyled>
    )
  }

  return (
    <PdfViewerStyled {...otherProps}>
      <object
        width='100%'
        height='100%'
        type='application/pdf'
        aria-label='PDF'
        data={data}
      >
        {children}
      </object>
    </PdfViewerStyled>
  )
}

PdfViewer.defaultProps = {}
PdfViewer.displayName = 'PdfViewer'
